/**
 * Character-guided profiling — shared web contract shapes (WU-W0).
 *
 * SINGLE SOURCE of the memory/trait wire shapes: derived verbatim from
 * requirements/00-FROZEN-CONTRACT.md v1.2. WU-W2/W3/W4 import from here — never
 * redeclare these shapes elsewhere (a second copy silently diverges from the backend).
 */

// ── §2.3 trait dimensions (declared first — used by UserMemory + others) ──
export type TraitDimensionKey =
  | 'social_energy'
  | 'openness'
  | 'emotional_expression'
  | 'stability_vs_adventure'
  | 'play_competitiveness'
  | 'romance_style'
  | 'relationship_pace';

// ── §1.6 core memory shapes ──
export type MemoryKind = 'preference' | 'value' | 'trait' | 'boundary' | 'fact';
export type MemorySource = 'conversation' | 'form' | 'behavior';
export type MemoryStatus = 'proposed' | 'confirmed' | 'rejected' | 'burned';

export interface MemoryStatusEvent {
  status: MemoryStatus;
  at: string;
  reason: string;
}

export interface UserMemory {
  id: string;
  userId: number;
  npcId: string | null;
  npcName?: string | null;
  kind: MemoryKind;
  text: string;
  tags: string[];
  polarity: -1 | 0 | 1;
  confidence: number; // 0..1
  source: MemorySource;
  status: MemoryStatus;
  dimension?: TraitDimensionKey | null;
  probeId?: string | null;
  suppressTopicFamily: boolean;
  statusHistory: MemoryStatusEvent[];
  createdAt: string;
  updatedAt: string;
}

export interface TraitDimensionValue {
  value: number;
  confidence: number;
  sampleSize: number;
}
export type TraitDimensions = Record<TraitDimensionKey, TraitDimensionValue>;

export interface UserTraitProfile {
  userId: number;
  dimensions: TraitDimensions;
  memoryCount: number;
  updatedAt: string;
}

/** Human-readable pole labels + the NPC who probes each dimension (§2.3). */
export const TRAIT_POLES: Record<TraitDimensionKey, { low: string; high: string; npc: string }> = {
  social_energy:          { low: 'Corner booth',            high: 'Front of the crowd',         npc: 'Mara' },
  openness:               { low: 'Familiar comforts',       high: 'Novelty seeker',             npc: 'Sister Lyra' },
  emotional_expression:   { low: 'Reserved',                high: 'Wears their heart out loud', npc: 'Rooftop Poet' },
  stability_vs_adventure: { low: 'Harbor keeper',           high: 'Horizon chaser',             npc: 'Old Tomas' },
  play_competitiveness:   { low: 'Play for fun',            high: 'In it to win it',            npc: 'Vex' },
  romance_style:          { low: 'Understated & pragmatic', high: 'Grand & romantic',           npc: 'Rooftop Poet' },
  relationship_pace:      { low: 'Slow and steady',         high: 'Fast spark',                 npc: 'Mara' },
};

/**
 * §2.4 tag → dimension controlled vocabulary (36 entries). MUST stay byte-identical
 * to the backend C# `TagVocabulary` — a divergence silently mis-scores traits.
 * `contribution` = which pole the tag pulls toward (before polarity).
 * `sharedPhrase` = neutral wording used ONLY in mutually-confirmed icebreakers.
 */
export const TAG_DIMENSION_MAP: Record<string, { dimension: TraitDimensionKey; contribution: -1 | 1; sharedPhrase: string }> = {
  // social_energy
  crowds:            { dimension: 'social_energy', contribution:  1, sharedPhrase: 'thrive in a full room' },
  party_energy:      { dimension: 'social_energy', contribution:  1, sharedPhrase: 'bring the party energy' },
  corner_booth:      { dimension: 'social_energy', contribution: -1, sharedPhrase: 'gravitate to the quiet corner' },
  solo_recharge:     { dimension: 'social_energy', contribution: -1, sharedPhrase: 'recharge in your own company' },
  small_circle:      { dimension: 'social_energy', contribution: -1, sharedPhrase: 'keep a small, close circle' },
  // openness
  novelty:           { dimension: 'openness', contribution:  1, sharedPhrase: 'chase the new and untried' },
  curious:           { dimension: 'openness', contribution:  1, sharedPhrase: 'follow your curiosity anywhere' },
  deep_dive:         { dimension: 'openness', contribution:  1, sharedPhrase: 'love going deep on things' },
  familiar_comfort:  { dimension: 'openness', contribution: -1, sharedPhrase: 'treasure the familiar' },
  routine:           { dimension: 'openness', contribution: -1, sharedPhrase: 'find comfort in routine' },
  // emotional_expression
  expressive:        { dimension: 'emotional_expression', contribution:  1, sharedPhrase: 'wear your feelings openly' },
  wears_heart:       { dimension: 'emotional_expression', contribution:  1, sharedPhrase: 'lead with the heart' },
  loud_love:         { dimension: 'emotional_expression', contribution:  1, sharedPhrase: 'love out loud' },
  guarded:           { dimension: 'emotional_expression', contribution: -1, sharedPhrase: 'keep your cards close' },
  reserved:          { dimension: 'emotional_expression', contribution: -1, sharedPhrase: 'hold feelings quietly' },
  quiet_love:        { dimension: 'emotional_expression', contribution: -1, sharedPhrase: 'love in the quiet ways' },
  // stability_vs_adventure
  adventure:         { dimension: 'stability_vs_adventure', contribution:  1, sharedPhrase: 'say yes to adventure' },
  horizon_chaser:    { dimension: 'stability_vs_adventure', contribution:  1, sharedPhrase: 'chase the horizon' },
  risk_taker:        { dimension: 'stability_vs_adventure', contribution:  1, sharedPhrase: 'take the leap' },
  harbor_keeper:     { dimension: 'stability_vs_adventure', contribution: -1, sharedPhrase: 'keep a steady harbor' },
  steady_anchor:     { dimension: 'stability_vs_adventure', contribution: -1, sharedPhrase: 'value a steady anchor' },
  homebody:          { dimension: 'stability_vs_adventure', contribution: -1, sharedPhrase: 'find joy close to home' },
  // play_competitiveness
  competitive:       { dimension: 'play_competitiveness', contribution:  1, sharedPhrase: 'play to win' },
  win_driven:        { dimension: 'play_competitiveness', contribution:  1, sharedPhrase: 'keep score' },
  rematch:           { dimension: 'play_competitiveness', contribution:  1, sharedPhrase: 'always want the rematch' },
  for_fun:           { dimension: 'play_competitiveness', contribution: -1, sharedPhrase: 'play just for the fun' },
  easygoing_play:    { dimension: 'play_competitiveness', contribution: -1, sharedPhrase: 'keep games easygoing' },
  high_five_move:    { dimension: 'play_competitiveness', contribution: -1, sharedPhrase: 'high-five and move on' },
  // romance_style
  grand_gesture:     { dimension: 'romance_style', contribution:  1, sharedPhrase: 'love a grand gesture' },
  romantic_idealist: { dimension: 'romance_style', contribution:  1, sharedPhrase: 'are a romantic at heart' },
  slow_burn:         { dimension: 'romance_style', contribution: -1, sharedPhrase: 'prefer a slow burn' },
  pragmatic_love:    { dimension: 'romance_style', contribution: -1, sharedPhrase: 'keep romance grounded' },
  // relationship_pace
  fast_spark:        { dimension: 'relationship_pace', contribution:  1, sharedPhrase: 'move on a fast spark' },
  dive_in:           { dimension: 'relationship_pace', contribution:  1, sharedPhrase: 'dive right in' },
  slow_pace:         { dimension: 'relationship_pace', contribution: -1, sharedPhrase: 'take it slow' },
  take_it_slow:      { dimension: 'relationship_pace', contribution: -1, sharedPhrase: 'let things unfold slowly' },
};

// ── §3.2 create request ──
export interface CreateMemoryRequest {
  clientRef: string;
  npcId?: string | null;
  kind: MemoryKind;
  text: string;
  tags: string[];
  polarity: -1 | 0 | 1;
  source: MemorySource;
  confidence?: number;
  status?: 'confirmed' | 'rejected';
  suppressTopicFamily?: boolean;
  probeId?: string;
}

// ── §4.1 /talk extended shapes ──
export interface MemoryProposal {
  clientRef: string;
  text: string;
  kind: MemoryKind;
  tags: string[];
  polarity: -1 | 0 | 1;
  dimension: TraitDimensionKey;
  npcId: string;
  probeId?: string;
  sensitive: false;
}
export interface TalkResponse {
  reply: string;
  memoryProposal?: MemoryProposal;
}

// ── §6.1 discovery shared memory ──
export interface SharedMemory {
  tag: string;
  dimension: TraitDimensionKey;
  phrase: string;
  npcName: string;
}
